import React, { useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetOfferForPrintQuery, useHealthQuestionnaireQuestionsQuery } from 'src/common/api/apollo/additional';
import { Flex } from 'src/components';
import { buildInitialQuestionnaireValuesFromOffer } from 'src/components/forms/healthQuestionnaire/buildInitialQuestionnaireValuesFromOffer.util';
import { HealthQuestionnaireForm } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.form';
import type { AnyQuestion } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.types';
import { AppRoutesAdditional } from '../../_router/app.routes';

const HealthQuestionnairPrintPage = () => {
  const params = useParams<{ offerId: string }>();
  const navigate = useNavigate();
  const [printReady, setPrintReady] = useState(false);

  const { data: getOfferAdditionalData } = useGetOfferForPrintQuery({
    variables: { id: params.offerId || '' },
    skip: !params.offerId,
    onError: () => {
      navigate(AppRoutesAdditional.OFFERS_PREVIEW.fullPath);
    }
  });

  const { data: getHealthQuestionnaireQuestionsData } = useHealthQuestionnaireQuestionsQuery({
    variables: { brandCode: getOfferAdditionalData?.getOfferForPrint?.product?.brand?.code ?? '', productCode: getOfferAdditionalData?.getOfferForPrint?.product?.code ?? '', dontExclude: true },
    skip: !getOfferAdditionalData?.getOfferForPrint?.product?.brand?.code || !getOfferAdditionalData?.getOfferForPrint?.product?.code,
    onError: () => {
      navigate(AppRoutesAdditional.OFFERS_PREVIEW.fullPath);
    },
    onCompleted: (data) => {
      if (data.healthQuestionnaireQuestions.length === 0) {
        navigate(`${AppRoutesAdditional.OFFER.fullPath}/${params.offerId}`);
      }
    }
  });

  const questionsList = getHealthQuestionnaireQuestionsData?.healthQuestionnaireQuestions;

  const initialValues = useMemo(
    () =>
      buildInitialQuestionnaireValuesFromOffer(
        (questionsList ?? []) as AnyQuestion[],
        getOfferAdditionalData?.getOfferForPrint?.productTypeSpecific?.healthQuestionnaire ?? null,
      ),
    [questionsList, getOfferAdditionalData?.getOfferForPrint?.productTypeSpecific?.healthQuestionnaire],
  );

  const dataLoaded = Boolean(getOfferAdditionalData && getHealthQuestionnaireQuestionsData);

  /** Signals headless PDF capture (Puppeteer) that layout and fonts are ready — see `[data-print-ready="true"]`. */
  useLayoutEffect(() => {
    if (!dataLoaded) {
      setPrintReady(false);
      return undefined;
    }

    setPrintReady(false);
    let cancelled = false;

    const signalReady = () => {
      if (!cancelled) setPrintReady(true);
    };

    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    fontsReady.catch(() => undefined).then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(signalReady);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [dataLoaded, initialValues]);

  if (!getOfferAdditionalData || !getHealthQuestionnaireQuestionsData) return null;

  return (
    <div data-print-ready={printReady ? 'true' : undefined}>
      <Flex vertical gap={24}>
        <HealthQuestionnaireForm
          questions={getHealthQuestionnaireQuestionsData.healthQuestionnaireQuestions}
          initialValues={initialValues}
          displayMode
        />
      </Flex>
    </div>
  );
};

export default HealthQuestionnairPrintPage;